import { useId, useLayoutEffect, useRef } from "react";
import { FaMinus, FaPlus } from "react-icons/fa";
import gsap from "gsap";
import styles from "./AccordianComp.module.css";

function RichAnswer({ answer }) {
  if (!answer) {
    return null;
  }

  if (typeof answer !== "string") {
    return answer;
  }

  return (
    <div
      className={styles.answerRichText}
      dangerouslySetInnerHTML={{ __html: answer }}
    />
  );
}

export default function AccordianComp({
  question,
  answer,
  isOpen = false,
  onToggle,
  number,
}) {
  const contentId = useId();
  const contentRef = useRef(null);
  const answerRef = useRef(null);
  const iconRef = useRef(null);
  const timelineRef = useRef(null);
  const initialRenderRef = useRef(true);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const answerElement = answerRef.current;
    const icon = iconRef.current;

    if (!content || !answerElement || !icon) {
      return;
    }

    timelineRef.current?.kill();

    const targetHeight = answerElement.offsetHeight;

    if (initialRenderRef.current) {
      gsap.set(content, {
        height: isOpen ? targetHeight : 0,
        opacity: isOpen ? 1 : 0,
        overflow: "hidden",
      });

      gsap.set(icon, {
        rotation: isOpen ? 180 : 0,
      });

      initialRenderRef.current = false;
      return;
    }

    const timeline = gsap.timeline({
      defaults: {
        ease: "power2.out",
      },
    });

    timelineRef.current = timeline;

    if (isOpen) {
      timeline
        .set(content, {
          height: 0,
          opacity: 0,
          overflow: "hidden",
        })
        .to(content, {
          height: targetHeight,
          opacity: 1,
          duration: 0.35,
        })
        .to(
          icon,
          {
            rotation: 180,
            duration: 0.25,
          },
          "<",
        );
    } else {
      timeline
        .to(content, {
          height: 0,
          opacity: 0,
          duration: 0.3,
        })
        .to(
          icon,
          {
            rotation: 0,
            duration: 0.25,
          },
          "<",
        );
    }

    return () => {
      timeline.kill();
    };
  }, [isOpen]);

  return (
    <article className={`${styles.item} ${isOpen ? styles.itemOpen : ""}`}>
      <button
        type="button"
        className={styles.trigger}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <span className={styles.questionGroup}>
          {number && (
            <span className={styles.number}>
              {String(number).padStart(2, "0")}
            </span>
          )}

          <span className={styles.question}>{question}</span>
        </span>

        <span ref={iconRef} className={styles.iconWrap} aria-hidden="true">
          {isOpen ? <FaMinus /> : <FaPlus />}
        </span>
      </button>

      <div
        ref={contentRef}
        id={contentId}
        className={styles.content}
        aria-hidden={!isOpen}
      >
        <div ref={answerRef} className={styles.answer}>
          <RichAnswer answer={answer} />
        </div>
      </div>
    </article>
  );
}
